import { useMemo, useState, useContext } from "react";
import DOMPurify from "dompurify";
import { getToken } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { AppStateContext } from "../../state/AppProvider";

import { ChatResponse, getCitationFilePath, updateFeedback } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { ButtonTooltip } from "../ButtonTooltip";

DOMPurify.addHook("afterSanitizeAttributes", node => {
    if (node.nodeName === "A") {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
    }
});

const TOOL_LABELS: Record<string, { label: string; icon: string }> = {
    azure_ai_search: { label: "Searched knowledge base", icon: "manage_search" },
    hibob_get_timeoff_balance: { label: "Checked leave balance", icon: "calendar_clock" },
    hibob_get_whos_out: { label: "Checked who's out", icon: "travel" },
    hibob_employee_search: { label: "Searched for employees", icon: "people" },
    hibob_get_my_goals: { label: "Checked goals", icon: "target" },
    hibob_get_goals_by_category: { label: "Checked goals", icon: "target" },
    hibob_get_goal_details: { label: "Checked goal details", icon: "target" }
};

interface Props {
    answer: ChatResponse;
    isStreaming: boolean;
    onCitationClicked: (filePath: string, citationTitle: string) => void;
    onThoughtProcessClicked: () => void;
    onCopyAnswerClicked: () => void;
    showThoughts?: boolean;
    lastAnswer: boolean;
    commentInput: string;
    setCommentInput: (commentInput: string) => void;
    setShowEmailDialog: (showEmailDialog: boolean) => void;
    showEmailDialog: boolean;
    windowWidth: number;
    toolCalls?: string[];
}

export const Answer = ({
    answer,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onCopyAnswerClicked,
    showThoughts,
    lastAnswer,
    setShowEmailDialog,
    showEmailDialog,
    toolCalls
}: Props) => {
    const client = useMsal().instance;
    const appStateContext = useContext(AppStateContext);

    const thoughts = answer.choices[0].context.metadata?.thoughts;
    const messageContent = answer.choices[0].message.content;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(messageContent, isStreaming, onCitationClicked), [answer]);
    const messageId = answer.choices[0].message_id;
    const feedback = answer.choices[0].feedback;

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    const sanitizedThoughts = DOMPurify.sanitize(thoughts!);

    const [copyIcon, setCopyIcon] = useState<string>("content_copy");

    let streamingDot: string =
        isStreaming && lastAnswer
            ? " <span class='material-symbols-outlined text-icon14 text-eq-charcoal' style=\"font-variation-settings: 'FILL' 1;\">circle</span>"
            : "";

    function onThoughtsClicked() {
        onThoughtProcessClicked();
    }

    function onCopyClicked() {
        onCopyAnswerClicked();
        setCopyIcon("check");
        setTimeout(() => {
            setCopyIcon("content_copy");
        }, 2000);
    }

    const updateMessageFeedback = async (feedback: string | null) => {
        const token = client ? await getToken(client) : undefined;
        await updateFeedback(
            {
                message_id: messageId || "",
                feedback: feedback
            },
            token?.accessToken
        ).then(res => {
            if (res) {
                appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY_MESSAGE", payload: res });
            }
        });
    };

    const onFeedbackHelpful = () => {
        if (feedback === "Unhelpful") {
            updateMessageFeedback("Helpful");
        } else if (feedback === "Helpful") {
            updateMessageFeedback(null);
        } else {
            updateMessageFeedback("Helpful");
        }
    };

    const onFeedbackUnhelpful = () => {
        if (feedback === "Helpful") {
            updateMessageFeedback("Unhelpful");
        } else if (feedback === "Unhelpful") {
            updateMessageFeedback(null);
        } else {
            updateMessageFeedback("Unhelpful");
        }
    };

    const onContactHuman = () => {
        setShowEmailDialog(true);
    };

    return (
        <div className="w-full">
            <div
                className={"transition-[padding] duration-200 py-[18px] px-4 text-base m-auto " + (appStateContext?.state.isChatHistoryOpen ? "" : "lg:pl-16")}
            >
                <div className="mx-auto flex flex-1 text-base gap-4 lg:gap-6 lg:max-w-3xl">
                    <div className="flex-shrink-0 flex flex-col relative items-end">
                        <div>
                            <div className="pt-0">
                                <div className="outline outline-2 outline-eq-green shadow-[0_0_2px_2px_#12AD2A] flex h-9 w-9 items-center justify-center overflow-hidden rounded-full">
                                    <div className="relative p-1 rounded-sm flex items-center justify-center h-8 w-8 text-[#000000]">
                                        <AnswerIcon />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="relative flex w-full min-w-0 flex-col">
                        <div className="flex-col gap-1 lg:gap-3">
                            <div className="flex flex-grow flex-col max-w-full">
                                <div className="flex flex-col gap-1 lg:gap-3">
                                    <div className="flex flex-grow flex-col max-w-full">
                                        <div className="min-h-[20px] text-message flex flex-col whitespace-pre-wrap break-words w-full items-end overflow-x-auto gap-2">
                                            <div className="flex w-full flex-col gap-1 first:pt-[3px]">
                                                <div className="w-full break-words">
                                                    {toolCalls && toolCalls.length > 0 && (
                                                        <div className="flex flex-col gap-1 mb-3">
                                                            {[...new Set(toolCalls)].map(tool => (
                                                                <div key={tool} className="flex items-center gap-1.5 text-sm text-eq-grey">
                                                                    <span className="material-symbols-outlined text-[1rem]">
                                                                        {TOOL_LABELS[tool]?.icon ?? "settings"}
                                                                    </span>
                                                                    <span>{TOOL_LABELS[tool]?.label ?? tool}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div
                                                        className="chat-stream stream-table text-base leading-[1.75]"
                                                        dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml + streamingDot }}
                                                    ></div>
                                                    <div className="">
                                                        {!!parsedAnswer.citations.length && <div className="font-semibold mb-1">Sources</div>}
                                                        {parsedAnswer.citations.map((x, i) => {
                                                            let citation = x.replace(/#page_label=(\d+)/, "");
                                                            let title = x.replace(/\.\w+#page=(\d+)/, "").replace(/\.json\?id=(.*)/, " ($1)");
                                                            title = title.replace(/#page_label=/, " - Page ");
                                                            let citationTitle = x.replace(/\.\w+#page=.*$/, "");
                                                            const path = getCitationFilePath(
                                                                x,
                                                                appStateContext?.state.assistant?.type === "Equiom"
                                                                    ? appStateContext?.state.assistant?.assistant
                                                                    : appStateContext?.state.assistant?.id,
                                                                appStateContext?.state.token?.accessToken
                                                            );
                                                            return (
                                                                <a
                                                                    key={i}
                                                                    className="flex flex-col text-eq-green hover:underline cursor-pointer"
                                                                    onClick={() => onCitationClicked(path, citationTitle)}
                                                                >
                                                                    {`${++i}. ${title}`}
                                                                </a>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {(!isStreaming || !lastAnswer) && (
                                        <div className="mt-2 flex gap-3 ml-[-3px]">
                                            <div className="items-center justify-start rounded-xl p-1 flex">
                                                <div className="flex items-center">
                                                    <span className="h-[30px] w-[30px]">
                                                        <ButtonTooltip title="Copy">
                                                            <button className="flex justify-center font-semibold text-eq-grey" onClick={() => onCopyClicked()}>
                                                                <span
                                                                    className={
                                                                        "material-symbols-outlined text-[1.25rem] " + (copyIcon === "check" && "text-eq-green")
                                                                    }
                                                                >
                                                                    {copyIcon}
                                                                </span>
                                                            </button>
                                                        </ButtonTooltip>
                                                    </span>
                                                    {!(sanitizedThoughts === "None") && (
                                                        <span className="h-[30px] w-[30px]">
                                                            <ButtonTooltip title={showThoughts ? "Hide thought process" : "Show thought process"}>
                                                                <button
                                                                    className={
                                                                        "flex justify-center font-semibold text-eq-grey pb-[3px]" +
                                                                        (showThoughts ? " icon-underline" : "")
                                                                    }
                                                                    onClick={() => onThoughtsClicked()}
                                                                    disabled={!thoughts}
                                                                >
                                                                    <span className="material-symbols-outlined text-eq-grey text-[1.25rem]">lightbulb</span>
                                                                </button>
                                                            </ButtonTooltip>
                                                        </span>
                                                    )}
                                                    <span className="h-[30px] w-[30px]">
                                                        <ButtonTooltip title="Good response">
                                                            <button
                                                                className={
                                                                    "flex justify-center font-semibold pb-[3px]" +
                                                                    (feedback === "Helpful" ? " icon-underline" : "")
                                                                }
                                                                onClick={onFeedbackHelpful}
                                                            >
                                                                <span className="material-symbols-outlined text-[1.25rem] text-eq-grey">thumb_up</span>
                                                            </button>
                                                        </ButtonTooltip>
                                                    </span>
                                                    <span className="h-[30px] w-[30px]">
                                                        <ButtonTooltip title="Bad response">
                                                            <button
                                                                className={
                                                                    "flex justify-center font-semibold pb-[3px]" +
                                                                    (feedback === "Unhelpful" ? " icon-underline" : "")
                                                                }
                                                                onClick={onFeedbackUnhelpful}
                                                            >
                                                                <span className="material-symbols-outlined text-[1.25rem] text-eq-grey">thumb_down</span>
                                                            </button>
                                                        </ButtonTooltip>
                                                    </span>
                                                    <span className="h-[30px] w-[30px]">
                                                        <ButtonTooltip title="Contact a human">
                                                            <button
                                                                className={
                                                                    "flex justify-center font-semibold text-eq-grey pb-[3px]" +
                                                                    (showEmailDialog ? " icon-underline" : "")
                                                                }
                                                                onClick={onContactHuman}
                                                            >
                                                                <span className="material-symbols-outlined text-[1.25rem]">emoji_people</span>
                                                            </button>
                                                        </ButtonTooltip>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {showThoughts && (
                                        <div className="card-shadow py-4 px-5">
                                            <div className="font-semibold text-xl mb-2">Thought process</div>
                                            <div className="stream-table" dangerouslySetInnerHTML={{ __html: sanitizedThoughts }}></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
